'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const useCases = [
  {
    tag: '[01]',
    title: 'New feature and service design review',
    description:
      'Before a line of code is written, TechSonar engineers review the design. Data flows, trust boundaries, authentication models, and authorisation logic are assessed against your actual threat landscape, not a generic checklist.',
  },
  {
    tag: '[02]',
    title: 'Infrastructure and architecture changes',
    description:
      'Cloud migrations, new integrations, microservices decomposition, each introduces new attack surface. We model the threats introduced by architectural change before the change is deployed.',
  },
  {
    tag: '[03]',
    title: 'Risk-led security programme design',
    description:
      'Security programmes built around your actual risk profile. We identify your highest-value assets, most likely threat actors, and most probable attack paths, then prioritise controls accordingly.',
  },
  {
    tag: '[04]',
    title: 'Zero-trust architecture design',
    description:
      'Identity-centric, least-privilege network architecture across cloud and on-premise environments. We design and implement zero-trust models that reduce lateral movement risk without breaking operational workflows.',
  },
];

const outcomes = [
  { metric: 'Pre-code', label: 'Security flaws identified before implementation' },
  { metric: '10x', label: 'Cheaper to fix in design than in production' },
  { metric: 'Sprint', label: 'Cadence, threat models updated with every major change' },
  { metric: 'Risk-led', label: 'Prioritisation based on your actual threat profile' },
];

const methodologies = [
  { name: 'STRIDE', desc: 'Spoofing, Tampering, Repudiation, Information Disclosure, Denial of Service, Elevation of Privilege, systematic threat identification across all system components.' },
  { name: 'PASTA', desc: 'Process for Attack Simulation and Threat Analysis, risk-centric methodology that aligns threat modelling with business objectives and risk appetite.' },
  { name: 'MITRE ATT&CK', desc: 'Adversary tactics and techniques mapped to your architecture. Threat models grounded in real-world attacker behaviour, not theoretical risk.' },
  { name: 'LINDDUN', desc: 'Privacy threat modelling for systems handling personal data. Linkability, Identifiability, Non-repudiation, Detectability, Disclosure, Unawareness, Non-compliance.' },
];

export default function ThreatModellingPage() {
  useScrollAnimation();

  return (
    <div className="min-h-screen bg-[#050a0f] text-[#f5f5f8] noise-overlay-animated">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="orb orb-blue" style={{ width: '500px', height: '500px', top: '-100px', left: '20%', opacity: 0.3 }} />
          <div className="orb orb-teal" style={{ width: '400px', height: '400px', bottom: '-50px', right: '0', opacity: 0.2 }} />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 mb-6 fade-in-up">
            <Link href="/" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Home</Link>
            <span className="text-[#333]">/</span>
            <Link href="/services" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Services</Link>
            <span className="text-[#333]">/</span>
            <span className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">Threat Modelling</span>
          </div>

          <div className="max-w-[900px]">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4 fade-in-up" style={{ transitionDelay: '0.05s' }}>
              [Threat Modelling & Security Architecture]
            </p>
            <h1 className="font-heading text-[clamp(2.5rem,6vw,4.5rem)] text-[#f5f5f8] font-medium leading-[1.05] mb-6 fade-in-up" style={{ transitionDelay: '0.1s' }}>
              Security designed in.<br />
              <span className="text-[#80edfa]">Not discovered after launch.</span>
            </h1>
            <p className="text-[#aaa] text-lg leading-relaxed max-w-[680px] mb-10 fade-in-up" style={{ transitionDelay: '0.15s' }}>
              Security flaws found in design cost a fraction of those found in production. Our engineers embed into your engineering process, reviewing new features, services, and infrastructure changes before they ship. Risk-led security programmes that reflect your actual threat landscape, not generic industry checklists.
            </p>
            <div className="flex flex-wrap gap-4 fade-in-up" style={{ transitionDelay: '0.2s' }}>
              <a href="#contact" className="btn-primary">Talk to a security architect</a>
              <a href="#use-cases" className="btn-secondary">See use cases</a>
            </div>
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="border-t border-[#222] py-16 bg-[#050a0f]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#222]">
            {outcomes?.map((o, i) => (
              <div key={i} className="bg-[#050a0f] px-8 py-10 fade-in-up" style={{ transitionDelay: `${i * 0.08}s` }}>
                <p className="font-heading text-[clamp(1.5rem,3vw,2.5rem)] text-[#80edfa] font-medium mb-2">{o?.metric}</p>
                <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest leading-relaxed">{o?.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section id="use-cases" className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-24 mb-16">
            <div className="lg:w-1/3 fade-in-left">
              <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4">Use cases</p>
              <h2 className="font-heading text-[clamp(1.75rem,3.5vw,2.75rem)] text-[#f5f5f8] font-medium leading-tight">
                Where threat modelling changes outcomes
              </h2>
            </div>
            <div className="lg:w-1/2 flex items-end fade-in-right" style={{ transitionDelay: '0.1s' }}>
              <p className="text-[#aaa] text-base leading-relaxed">
                Threat modelling is most valuable when it is embedded into the engineering process, not conducted as a one-off exercise. TechSonar engineers work at the design stage, in sprint reviews, and during architectural change.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#222]">
            {useCases?.map((uc, i) => (
              <div key={i} className="bg-[#050a0f] p-8 lg:p-10 fade-in-up" style={{ transitionDelay: `${i * 0.08}s` }}>
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">{uc?.tag}</p>
                <h3 className="font-heading text-xl text-[#f5f5f8] font-medium mb-3">{uc?.title}</h3>
                <p className="text-sm text-[#aaa] leading-relaxed">{uc?.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Methodologies */}
      <section className="border-t border-[#222] py-20 lg:py-28 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-14 fade-in-up">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4">Methodologies</p>
            <h2 className="font-heading text-[clamp(1.75rem,3.5vw,2.75rem)] text-[#f5f5f8] font-medium leading-tight max-w-[600px]">
              Industry-standard frameworks, applied by senior engineers
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {methodologies?.map((m, i) => (
              <div key={i} className="border border-[#222] bg-[#181b1f] p-8 fade-in-up" style={{ transitionDelay: `${i * 0.08}s` }}>
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-3">{m?.name}</p>
                <p className="text-sm text-[#aaa] leading-relaxed">{m?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Architecture Services */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row gap-16 items-start">
            <div className="lg:w-1/2 fade-in-left">
              <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4">Security architecture</p>
              <h2 className="font-heading text-[clamp(1.75rem,3.5vw,2.75rem)] text-[#f5f5f8] font-medium leading-tight mb-6">
                Architecture that reduces attack surface by design
              </h2>
              <p className="text-[#aaa] text-base leading-relaxed mb-8">
                Beyond threat modelling, TechSonar engineers design and review security architectures, from zero-trust network models to identity federation, from microservices security patterns to cloud-native control planes. Architecture that treats security as a first-class requirement.
              </p>
              <ul className="flex flex-col gap-4">
                {[
                  'Zero-trust architecture design and implementation',
                  'Identity and access management architecture',
                  'Microservices and API security patterns',
                  'Cloud security architecture across AWS, Azure, GCP',
                  'Security architecture review embedded in sprint process',
                  'Attack surface analysis and control gap identification',
                ]?.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <img src="https://cdn.prod.website-files.com/68a77090b95f85ab69076143/68fba43b700c01c8947096af_checkbox.svg" alt="" className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-[#aaa]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:w-1/2 fade-in-right" style={{ transitionDelay: '0.1s' }}>
              <div className="border border-[#222] bg-[#181b1f] p-8 mb-4">
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-3">The cost of late discovery</p>
                <p className="text-sm text-[#aaa] leading-relaxed mb-6">
                  IBM's Cost of a Data Breach report consistently shows that vulnerabilities identified in the design phase cost 6–10x less to remediate than those found in production. Threat modelling is not a compliance exercise, it is an engineering investment with measurable ROI.
                </p>
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { stage: 'Design', cost: '£1x', color: 'text-[#80edfa]' },
                    { stage: 'Development', cost: '£6x', color: 'text-[#aaa]' },
                    { stage: 'Production', cost: '£60x', color: 'text-[#666]' },
                  ]?.map((s, i) => (
                    <div key={i} className="text-center">
                      <p className={`font-heading text-2xl font-medium mb-1 ${s?.color}`}>{s?.cost}</p>
                      <p className="font-mono-brand text-[0.6rem] text-[#444] uppercase tracking-widest">{s?.stage}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="border border-[#222] bg-[#181b1f] p-8">
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-3">Embedded into your process</p>
                <p className="text-sm text-[#aaa] leading-relaxed">
                  TechSonar threat modelling is not a quarterly workshop. Our engineers attend design reviews, review PRs for security implications, and flag architectural concerns in real time, at the speed your engineering team moves.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#222] py-20 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div className="fade-in-left">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-3">Get started</p>
            <h2 className="font-heading text-[clamp(1.5rem,3vw,2.25rem)] text-[#f5f5f8] font-medium">
              Build security into your architecture from day one.
            </h2>
          </div>
          <div className="flex flex-wrap gap-4 fade-in-right" style={{ transitionDelay: '0.1s' }}>
            <a href="#contact" className="btn-primary">Book a scoping call</a>
            <Link href="/services" className="btn-secondary">View all services</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
